import React from 'react';
import { Translate } from 'react-redux-i18n';

const KPI_TONES = ['blue', 'teal', 'aqua', 'orange', 'purple', 'lime', 'red', 'indigo'];

export function formatRevenue(value) {
  const n = Number(value) || 0;
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatKpiValue(value, format) {
  if (format === 'revenue') return formatRevenue(value);
  return Number(value) || 0;
}

export function AnalyticsKpiGrid({ items, className = '' }) {
  if (!items || !items.length) return null;

  return (
    <div className={`analytics-kpi-grid ${className}`.trim()}>
      {items.map((item, index) => {
        const tone = item.tone || KPI_TONES[index % KPI_TONES.length];
        return (
          <div className={`analytics-kpi analytics-kpi--${tone}`} key={item.key || item.label || index}>
            <div className="analytics-kpi__card">
              <span className="analytics-kpi__glow" aria-hidden="true" />
              <p className="analytics-kpi__label">
                {item.labelKey ? <Translate value={item.labelKey} /> : item.label}
              </p>
              <p className="analytics-kpi__value">{item.value}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function AnalyticsBarList({
  rows,
  labelKey = 'label',
  valueKey = 'value',
  formatValue,
  tone = '',
  emptyKey = 'analyticsEmpty',
}) {
  if (!rows || !rows.length) {
    return (
      <p className="margin-top-10">
        <Translate value={emptyKey} />
      </p>
    );
  }

  const max = Math.max(...rows.map((row) => Number(row[valueKey]) || 0), 1);

  return (
    <ul className={`analytics-bar-list ${tone ? `analytics-bar-list--${tone}` : ''}`.trim()}>
      {rows.map((row) => {
        const value = Number(row[valueKey]) || 0;
        const pct = Math.max(2, Math.round((value / max) * 100));
        const label = row[labelKey];
        const display = formatValue ? formatValue(value, row) : value;
        return (
          <li className="analytics-bar-list__item" key={row.key || label}>
            <span className="analytics-bar-list__label" title={label}>{label}</span>
            <span className="analytics-bar-list__value">{display}</span>
            <div className="analytics-bar-list__track">
              <div className="analytics-bar-list__fill" style={{ width: `${pct}%` }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function buildLinePath(values, width, height, padding) {
  const innerW = width - padding * 2;
  const innerH = height - padding * 2;
  const max = Math.max(...values, 1);
  const step = values.length > 1 ? innerW / (values.length - 1) : 0;

  const coords = values.map((value, index) => {
    const x = padding + step * index;
    const y = padding + innerH - (value / max) * innerH;
    return { x, y };
  });

  const line = coords.map((point, index) => (
    `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`
  )).join(' ');

  const area = coords.length
    ? `${line} L ${coords[coords.length - 1].x.toFixed(1)} ${(height - padding).toFixed(1)} L ${coords[0].x.toFixed(1)} ${(height - padding).toFixed(1)} Z`
    : '';

  return { line, area, coords, max };
}

export function AnalyticsLineChart({
  points,
  valueKey = 'value',
  labelKey = 'dt',
  formatValue,
  color = '#348fe2',
  height = 220,
}) {
  if (!points || !points.length) {
    return (
      <p className="margin-top-10">
        <Translate value="analyticsEmpty" />
      </p>
    );
  }

  const width = 640;
  const padding = 28;
  const values = points.map((point) => Number(point[valueKey]) || 0);
  const { line, area, coords, max } = buildLinePath(values, width, height, padding);

  const format = (value) => (formatValue ? formatValue(value) : value);

  return (
    <div className="analytics-line-chart">
      <svg
        className="analytics-line-chart__svg"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="line chart"
      >
        {[0.25, 0.5, 0.75].map((ratio) => {
          const y = padding + (height - padding * 2) * ratio;
          return (
            <line
              key={ratio}
              x1={padding}
              x2={width - padding}
              y1={y}
              y2={y}
              className="analytics-line-chart__grid"
            />
          );
        })}
        <path d={area} className="analytics-line-chart__area" style={{ fill: color }} />
        <path d={line} className="analytics-line-chart__line" style={{ stroke: color }} />
        {coords.map((point, index) => (
          <circle
            key={points[index][labelKey] || index}
            cx={point.x}
            cy={point.y}
            r={4}
            className="analytics-line-chart__dot"
            style={{ fill: color }}
          >
            <title>
              {String(points[index][labelKey] || '').slice(5)}
              {': '}
              {format(values[index])}
            </title>
          </circle>
        ))}
      </svg>
      <div className="analytics-line-chart__axis">
        <span>{String(points[0][labelKey] || '').slice(5)}</span>
        <span className="analytics-line-chart__max">{format(max)}</span>
        <span>{String(points[points.length - 1][labelKey] || '').slice(5)}</span>
      </div>
    </div>
  );
}

export function AnalyticsMetricPills({ options, value, onChange }) {
  if (!options || !options.length) return null;

  return (
    <div className="analytics-metric-pills" role="group" aria-label="metric">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`analytics-metric-pills__btn ${value === option.value ? 'is-active' : ''}`}
          onClick={() => onChange(option.value)}
        >
          {option.labelKey ? <Translate value={option.labelKey} /> : option.label}
        </button>
      ))}
    </div>
  );
}

export function AnalyticsSpark({ points, valueKey = 'value', labelKey = 'dt' }) {
  if (!points || !points.length) {
    return (
      <p className="margin-top-10">
        <Translate value="analyticsEmpty" />
      </p>
    );
  }

  const max = Math.max(...points.map((p) => Number(p[valueKey]) || 0), 1);

  return (
    <div className="analytics-spark" role="img" aria-label="timeseries">
      {points.map((point) => {
        const value = Number(point[valueKey]) || 0;
        const height = Math.max(4, Math.round((value / max) * 100));
        const day = String(point[labelKey] || '').slice(5);
        return (
          <div className="analytics-spark__col" key={point[labelKey]}>
            <span className="analytics-spark__tip">{value}</span>
            <div className="analytics-spark__bar-wrap">
              <div className="analytics-spark__bar" style={{ height: `${height}%` }} />
            </div>
            <span className="analytics-spark__day">{day}</span>
          </div>
        );
      })}
    </div>
  );
}

export function AnalyticsFunnel({ steps, formatLabel }) {
  if (!steps || !steps.length) {
    return (
      <p className="margin-top-10">
        <Translate value="analyticsEmpty" />
      </p>
    );
  }

  const max = Math.max(...steps.map((s) => Number(s.value) || 0), 1);

  return (
    <ol className="analytics-funnel">
      {steps.map((step, index) => {
        const value = Number(step.value) || 0;
        const pct = Math.max(3, Math.round((value / max) * 100));
        const prev = index > 0 ? Number(steps[index - 1].value) || 0 : null;
        const conv = prev > 0 ? Math.round((value / prev) * 100) : null;
        const name = formatLabel ? formatLabel(step) : step.id;
        return (
          <li className="analytics-funnel__step" key={step.id}>
            <div className="analytics-funnel__head">
              <span className="analytics-funnel__name">{name}</span>
              <span className="analytics-funnel__meta">
                {value}
                {conv != null ? ` · ${conv}%` : ''}
              </span>
            </div>
            <div className="analytics-funnel__track">
              <div className="analytics-funnel__fill" style={{ width: `${pct}%` }} />
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export function AnalyticsPanel({ titleKey, children }) {
  return (
    <div className="analytics-panel">
      {titleKey && (
        <h4 className="analytics-panel__title">
          <Translate value={titleKey} />
        </h4>
      )}
      {children}
    </div>
  );
}
